import { useEffect, useState } from "react";
import { getDevAccounts, selectDevAccount } from "../lib/client";
import type { DevAccount } from "../lib/types";

/**
 * Shown instead of the normal app when DEV_AUTH_ENABLED=true and no dev user
 * has been picked yet. Stands in for the real Cognito login when
 * platform-service-main is running locally: picking an account here signs in
 * as that ps-main user via its `Authorization: Developer <id>` header.
 */
export function DevAuthPicker() {
  const [accounts, setAccounts] = useState<DevAccount[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selecting, setSelecting] = useState<string | null>(null);

  useEffect(() => {
    getDevAccounts()
      .then((accts) =>
        setAccounts(
          [...accts].sort(
            (a, b) =>
              Number(b.isSuperuser) - Number(a.isSuperuser) ||
              a.email.localeCompare(b.email),
          ),
        ),
      )
      .catch((err) =>
        setError(
          err instanceof Error ? err.message : "Failed to load dev accounts.",
        ),
      );
  }, []);

  async function pick(account: DevAccount) {
    setSelecting(account.id);
    setError(null);
    try {
      await selectDevAccount(account.id);
      window.location.reload();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to select dev account.",
      );
      setSelecting(null);
    }
  }

  return (
    <div className="app dev-auth-gate">
      <h1>Pick a developer account</h1>
      <p className="subtitle">
        platform-service-main is running in local/test mode — sign in as one of
        its debug accounts instead of via Cognito. Mechanicus is superuser-only,
        so non-superusers are shown but will be rejected.
      </p>
      {error && <p className="hint error">{error}</p>}
      {!accounts && !error && <p className="hint">Loading accounts…</p>}
      {accounts && accounts.length === 0 && (
        <p className="hint">No debug accounts found.</p>
      )}
      {accounts && accounts.length > 0 && (
        <ul className="dev-account-list">
          {accounts.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                className="dev-account-btn"
                disabled={selecting !== null}
                onClick={() => pick(a)}
              >
                <span className="dev-account-info">
                  <span className="dev-account-email">{a.email}</span>
                  <span className="dev-account-id">{a.id}</span>
                </span>
                <span
                  className={`dev-account-badge${a.isSuperuser ? "" : " muted"}`}
                >
                  {a.isSuperuser ? "superuser" : "not a superuser"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
